import React, { useState } from 'react';
import SmartTimeInput from './SmartTimeInput';
import DatePicker from '../common/DatePicker';
import useMediaQuery from '../../hooks/useMediaQuery';

/**
 * BlockTimeModal — create a doctor calendar block (Lunch / Training / Leave),
 * one-off or recurring. Uses one calendar (single date, or a range for a
 * multi-day leave) per the friendly date-picker UX.
 */

const PRESETS = ['Rounds', 'Surgery/OT', 'On-Call', 'Emergency', 'Lunch', 'Leave'];
const WD = [['Mon', 0], ['Tue', 1], ['Wed', 2], ['Thu', 3], ['Fri', 4], ['Sat', 5], ['Sun', 6]];

function todayISO() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

function fmtDisplay(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const p = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${p}`;
}

export default function BlockTimeModal({ isDark, defaultDate, defaultTime, busy, onSave, onClose }) {
  const [label, setLabel] = useState('');
  const [blocking, setBlocking] = useState(false);
  const [allDay, setAllDay] = useState(false);
  const [startTime24, setStartTime24] = useState(defaultTime || null);
  const [timeRaw, setTimeRaw] = useState(defaultTime ? fmtDisplay(defaultTime) : '');
  const [duration, setDuration] = useState(60);
  const [repeat, setRepeat] = useState('NONE');
  const [weekdays, setWeekdays] = useState([]);
  const [ongoing, setOngoing] = useState(true);
  const [singleDate, setSingleDate] = useState(defaultDate || todayISO());
  const [range, setRange] = useState({ start: defaultDate || todayISO(), end: null });
  const [err, setErr] = useState('');
  const isNarrow = useMediaQuery('(max-width: 640px)');

  const recurring = repeat !== 'NONE';
  const border = isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)';
  const text = isDark ? '#f5f5f7' : '#111827';
  const muted = isDark ? '#9ca3af' : '#6b7280';

  const chip = (active) => ({
    padding: '0.375rem 0.625rem', borderRadius: '0.5rem', cursor: 'pointer',
    border: `1px solid ${active ? '#0d9488' : border}`,
    background: active ? '#0d9488' : (isDark ? '#1e293b' : '#ffffff'),
    color: active ? '#fff' : (isDark ? '#e2e8f0' : '#334155'),
    fontSize: '0.75rem', fontWeight: 600,
  });

  const toggleWeekday = (i) => {
    setWeekdays((w) => (w.includes(i) ? w.filter((x) => x !== i) : [...w, i]));
  };

  const submit = () => {
    if (!label.trim()) { setErr('Add a label (e.g. Lunch).'); return; }
    if (!allDay && !startTime24) { setErr('Pick a start time (or choose All day).'); return; }
    if (repeat === 'WEEKLY' && weekdays.length === 0) { setErr('Pick at least one weekday.'); return; }
    if (recurring && !ongoing && !range.end) { setErr('Pick the end date of the range.'); return; }

    const payload = {
      label: label.trim(),
      blocking,
      all_day: allDay,
      repeat,
      weekdays: repeat === 'WEEKLY' ? weekdays : null,
    };
    if (!allDay) { payload.start_time = startTime24; payload.duration_minutes = duration; }
    if (repeat === 'NONE') {
      payload.start_date = singleDate;
    } else if (ongoing) {
      payload.start_date = singleDate;
      payload.end_date = null;
    } else {
      payload.start_date = range.start;
      payload.end_date = range.end;
    }
    setErr('');
    onSave(payload);
  };

  const labelStyle = { fontSize: '0.6875rem', fontWeight: 700, color: muted, textTransform: 'uppercase', letterSpacing: '0.02em', marginBottom: '0.375rem', display: 'block' };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 9999, display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: '1rem',
      background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        width: '100%', maxWidth: '420px', maxHeight: '90vh', overflowY: 'auto',
        boxSizing: 'border-box',
        borderRadius: '1rem', padding: isNarrow ? '0.875rem' : '1.25rem',
        background: isDark ? '#1e1e24' : '#fff', border: `1px solid ${border}`,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <div style={{ fontSize: '1rem', fontWeight: 700, color: text, marginBottom: '0.875rem' }}>⏱ Block time</div>

        {/* Label */}
        <label style={labelStyle}>Label</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginBottom: '0.5rem' }}>
          {PRESETS.map((p) => (
            <button key={p} type="button" onClick={() => setLabel(p)} style={chip(label === p)}>{p}</button>
          ))}
        </div>
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Or type a custom label…"
          style={{
            width: '100%', padding: '0.5rem 0.625rem', borderRadius: '0.5rem', boxSizing: 'border-box',
            border: `1px solid ${border}`, background: isDark ? 'rgba(255,255,255,0.06)' : '#fff',
            color: text, fontSize: '0.8125rem', marginBottom: '0.875rem',
          }} />

        {/* Blocking vs non-blocking */}
        <label style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
          Visibility
          <span title="Blocking: shows in red on the booking screen so staff see the doctor is blocked. Non-blocking: just your calendar note, hidden from the booking screen."
            style={{ cursor: 'help', color: muted, fontWeight: 400 }}>ℹ️</span>
        </label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginBottom: '0.375rem' }}>
          <button type="button" onClick={() => setBlocking(false)} style={chip(!blocking)}>🔖 Non-blocking</button>
          <button type="button" onClick={() => setBlocking(true)} style={chip(blocking)}>🔒 Blocking</button>
        </div>
        <div style={{ fontSize: '0.6875rem', color: muted, marginBottom: '0.875rem' }}>
          {blocking
            ? 'Shows in red on the booking screen so staff see the doctor is blocked.'
            : 'Private calendar note — hidden from the booking screen; booking is unaffected.'}
        </div>

        {/* All day */}
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
          <span style={{ fontSize: '0.8125rem', color: text, fontWeight: 600 }}>All day</span>
        </label>

        {/* Time + duration (when not all-day) */}
        {!allDay && (
          <div style={{ marginBottom: '0.875rem' }}>
            <label style={labelStyle}>Start time</label>
            <SmartTimeInput value={timeRaw} onChange={setTimeRaw}
              onResolve={(d, h, t24) => setStartTime24(t24)} isDark={isDark} placeholder="e.g. 1:00 PM" />
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
              <span style={{ fontSize: '0.75rem', color: muted }}>Duration</span>
              <button type="button" onClick={() => setDuration((d) => Math.max(5, d - 5))} style={chip(false)}>−</button>
              <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: text, minWidth: '4rem', textAlign: 'center' }}>{duration} min</span>
              <button type="button" onClick={() => setDuration((d) => Math.min(600, d + 5))} style={chip(false)}>+</button>
            </div>
          </div>
        )}

        {/* Repeat */}
        <label style={labelStyle}>Repeat</label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginBottom: '0.5rem' }}>
          {[['NONE', 'Does not repeat'], ['DAILY', 'Daily'], ['WEEKLY', 'Weekly']].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setRepeat(v)} style={chip(repeat === v)}>{l}</button>
          ))}
        </div>
        {repeat === 'WEEKLY' && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3125rem', marginBottom: '0.625rem' }}>
            {WD.map(([l, i]) => (
              <button key={i} type="button" onClick={() => toggleWeekday(i)} style={{ ...chip(weekdays.includes(i)), padding: '0.3125rem 0.5rem' }}>{l}</button>
            ))}
          </div>
        )}

        {/* Dates */}
        <label style={labelStyle}>{repeat === 'NONE' ? 'Date' : (ongoing ? 'Starts' : 'Dates')}</label>
        {recurring && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={ongoing} onChange={(e) => setOngoing(e.target.checked)} />
            <span style={{ fontSize: '0.75rem', color: muted }}>Ongoing (no end date)</span>
          </label>
        )}
        <div style={{ marginBottom: '0.875rem' }}>
          {/* No `min` — blocks may be in the past (a block doubles as a doctor's log). */}
          {repeat === 'NONE' || ongoing ? (
            <DatePicker mode="single" value={singleDate} inline isDark={isDark} onChange={setSingleDate} />
          ) : (
            <DatePicker mode="range" range={range} inline isDark={isDark} onChange={setRange} />
          )}
        </div>

        {err && <div style={{ fontSize: '0.75rem', color: '#ef4444', marginBottom: '0.625rem' }}>{err}</div>}

        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={{
            padding: '0.5rem 0.875rem', borderRadius: '0.5rem', cursor: 'pointer',
            border: `1px solid ${border}`, background: isDark ? '#1e293b' : '#fff',
            color: isDark ? '#e2e8f0' : '#334155', fontSize: '0.8125rem', fontWeight: 600,
          }}>Cancel</button>
          <button type="button" disabled={busy} onClick={submit} style={{
            padding: '0.5rem 1rem', borderRadius: '0.5rem', cursor: busy ? 'not-allowed' : 'pointer',
            border: 'none', background: '#0d9488', color: '#fff', fontSize: '0.8125rem', fontWeight: 700,
            opacity: busy ? 0.6 : 1,
          }}>{busy ? 'Saving…' : 'Save block'}</button>
        </div>
      </div>
    </div>
  );
}
